import { useState, useEffect, useCallback } from "react";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import { SortOptionType } from "@/lib/Types/InspirationSortFilter";
import { EnvConfig } from "../Helpers/getServerConfig";
import { sendKibanaLog } from "@/app/api/sendKibanaLog";

export const useSortLogic = (
  sortOptions: SortOptionType[],
  defaultSortOption: SortOptionType,
  envConfig: EnvConfig,
): {
  selectedSort: string;
  setSelectedSort: (value: string) => void;
  currentOption: SortOptionType | undefined;
} => {
  const searchParams = useReactiveSearchParams();
  const searchId = searchParams.get("searchId") ?? "";

  // Get current sort from URL or use default
  const getCurrentSortFromUrl = useCallback((): string => {
    const sortFromUrl = searchParams.get("sort");
    if (sortFromUrl) {
      const matchingOption = sortOptions.find(
        (option) => option.value === sortFromUrl,
      );
      return matchingOption ? matchingOption.value : defaultSortOption.value;
    }
    return defaultSortOption.value;
  }, [searchParams, sortOptions, defaultSortOption]);

  // Keep local state in sync with URL
  const [selectedSort, setSelectedSortState] = useState<string>(
    getCurrentSortFromUrl,
  );

  // Update when URL changes externally
  useEffect(() => {
    setSelectedSortState(getCurrentSortFromUrl());
  }, [searchParams, getCurrentSortFromUrl]);

  // Function to update sort (updates URL directly)
  const setSelectedSort = useCallback(
    (newSortValue: string): void => {
      // Don't update if value hasn't changed
      if (newSortValue === selectedSort) {
        return;
      }

      // Update state immediately for responsive UI
      setSelectedSortState(newSortValue);

      sendKibanaLog({
        envConfig: envConfig,
        message: `Sort Changed: ${newSortValue}`,
        category: "interaction",
        searchId: searchId,
      });

      // Get current params
      const current = new URLSearchParams(window.location.search);

      // Only update URL if different from current value
      if (current.get("sort") !== newSortValue) {
        current.set("sort", newSortValue);

        // Use history.pushState to create a history entry for back/forward navigation
        window.history.pushState(
          {},
          "",
          `${window.location.pathname}?${current.toString()}`,
        );
        // Dispatch urlchange so useReactiveSearchParams picks up the change.
        window.dispatchEvent(new Event("urlchange"));
      }
    },
    [selectedSort, envConfig, searchId],
  );

  // Current option object
  const currentOption = sortOptions.find(
    (option) => option.value === selectedSort,
  );

  return {
    selectedSort,
    setSelectedSort,
    currentOption,
  };
};
